import { ProjectService } from './project.service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { Project } from '../projects/domain/project';
import { EmployeeConflict } from '../shared/models/employee-conflict';
import { PhaseWithEmployees } from '../projects/domain/phase-with-employees';
import { EmployeeService } from '../employees/services/employee.service';
import { financialProtectionAvailable } from '../shared/models/financial-protection';
import moment from 'moment';

export interface AssignmentDateRequest {
  employeeId: string;
  startWork?: string | null;
  endWork?: string | null;
}

export interface AssignmentDatePreview {
  phase: PhaseWithEmployees | null;
  warnings: string[];
}

export interface AssignmentDateResult {
  message: string;
  adjusted: boolean;
  unverified: boolean;
}

export interface AssignEmployeeData {
  startWork: string;
  endWork: string;
  hourlyWage: number;
  professionId: number;
}

@Injectable({
  providedIn: 'root',
})
export class AssignEmployeeService {
  private project = new BehaviorSubject<Project>(undefined);
  private phase = new BehaviorSubject<PhaseWithEmployees>(undefined);
  private employeeIds = new BehaviorSubject<string[]>([]);

  project$ = this.project.asObservable();
  phase$ = this.phase.asObservable();
  employeeIds$ = this.employeeIds.asObservable();

  constructor(private httpClient: HttpClient, private projectService: ProjectService, private employeeService: EmployeeService) {}

  setProject(project: Project) {
    this.project.next(project);
  }

  setPhase(phase: PhaseWithEmployees) {
    this.phase.next(phase);
  }

  setEmployeeIds(ids: string[]) {
    this.employeeIds.next(ids);
  }

  assignEmployeeToPhase(employeeIds: string[], projectId: string, phaseId: string) {
    const objects = employeeIds.map(i => ({ employeeId: i }));
    return this.httpClient.post(`${ environment.api }/project/${ projectId }/phase/${ phaseId }/employee`, {
      employees: [...objects],
    }).pipe(switchMap(() => this.projectService.getProjectPhasesWithEmployees(projectId, true).pipe(
      catchError(() => throwError(() => new Error('The employees were assigned, but we couldn’t display their saved work dates. Refresh the project to check the assignments. Do not submit them again.'))),
    )));
  }

  previewAssignmentDates(projectId: string, phaseId: string, requests: AssignmentDateRequest[]): Observable<AssignmentDatePreview> {
    const selection = requests.map(request => ({ ...request }));
    const employeeIds = [...new Set(selection.map(request => request.employeeId))];
    return forkJoin({
      phases: this.projectService.getProjectPhasesWithEmployees(projectId, true).pipe(take(1)),
      employees: employeeIds.length ? forkJoin(employeeIds.map(id => this.employeeService.getEmployee(id, true).pipe(take(1)))) : of([]),
    }).pipe(
      map(({ phases, employees }) => {
        const phase = phases.find(item => item.id === phaseId);
        if (!Array.isArray(phase?.employees)) throw new Error('Assignment dates unavailable');
        const warnings: string[] = [];
        for (const request of selection) {
          const employee = employees.find(item => item.id === request.employeeId);
          if (!employee || !financialProtectionAvailable(employee.financialProtection)) throw new Error('Assignment dates unavailable');
          const range = this.requestedWorkDates(request, phase);
          if (!range.from || !range.to) continue;
          let start = range.from;
          for (const period of employee.financialProtection.months) {
            if (range.from <= period.to && range.to >= period.from) {
              const nextStart = moment(period.to).add(1, 'day').format('YYYY-MM-DD');
              if (nextStart > start) start = nextStart;
            }
          }
          if (start !== range.from && start <= range.to) {
            const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || request.employeeId;
            warnings.push(`${name}: planned work dates ${start} – ${range.to} instead of ${range.from} – ${range.to}. Protected financial periods are excluded.`);
          }
        }
        return { phase: { ...phase, employees: phase.employees.map(employee => ({ ...employee })) }, warnings };
      }),
      catchError(() => of({ phase: null, warnings: ['We couldn’t preview the work dates for this selection. You can continue, then refresh the project to check the saved dates.'] })),
    );
  }

  assignmentDateResult(before: PhaseWithEmployees | null, phases: PhaseWithEmployees[], phaseId: string, requests: AssignmentDateRequest[]): AssignmentDateResult {
    const unverified = { message: ' The changes were saved, but we couldn’t identify their new work dates. Refresh the project to check the result. Do not submit them again.', adjusted: false, unverified: true };
    const after = phases?.find(phase => phase.id === phaseId);
    if (!Array.isArray(before?.employees) || !Array.isArray(after?.employees)) return unverified;
    const employeeIds = new Set(requests.map(request => request.employeeId));
    if (before.employees.some(employee => employeeIds.has(employee.employeeId) && !employee.id)) return unverified;
    const previousIds = new Set(before.employees.map(employee => employee.id));
    const saved = after.employees.filter(employee => employeeIds.has(employee.employeeId) && !previousIds.has(employee.id));
    if (saved.length !== requests.length || saved.some(employee => !employee.id) || new Set(saved.map(employee => employee.id)).size !== saved.length
      || requests.some(request => saved.filter(employee => employee.employeeId === request.employeeId).length !== requests.filter(item => item.employeeId === request.employeeId).length)) return unverified;
    const remaining = [...requests];
    const changed = saved.filter(employee => {
      const actual = this.requestedWorkDates(employee, after);
      const index = remaining.findIndex(request => {
        const expected = this.requestedWorkDates(request, before);
        return request.employeeId === employee.employeeId && expected.from === actual.from && expected.to === actual.to;
      });
      if (index < 0) return true;
      remaining.splice(index, 1);
      return false;
    });
    if (!changed.length) return { message: '', adjusted: false, unverified: false };
    const dates = changed.map(employee => {
      const range = this.requestedWorkDates(employee, after);
      return `${[employee.firstName, employee.lastName].filter(Boolean).join(' ') || employee.employeeId}: ${range.from || 'an open start'} – ${range.to || 'an open end'}`;
    }).join('; ');
    return { message: ` Saved work dates differ from the requested dates: ${dates}.`, adjusted: true, unverified: false };
  }

  private requestedWorkDates(request: AssignmentDateRequest, phase: PhaseWithEmployees): { from: string | null; to: string | null } {
    return request.startWork && request.endWork
      ? { from: request.startWork, to: request.endWork }
      : { from: phase.startDate || null, to: phase.endDate || null };
  }

  removeEmployeeFromPhase(employeeOnPhaseIds: string[], projectId: string, phaseId: string) {
    return this.projectService.verifyFinancialRemoval(projectId, phaseId, undefined, employeeOnPhaseIds).pipe(switchMap(() =>
      this.httpClient.request('delete', `${ environment.api }/project/${ projectId }/phase/${ phaseId }/employee`, { body: { ids: employeeOnPhaseIds } })));
  }

  adjustEmployeeInPhase(employeeOnPhaseIds: string[], projectId: string, phaseId: string, assignEmployeeData: Partial<AssignEmployeeData>) {
    return this.httpClient.put(`${ environment.api }/project/${ projectId }/phase/${ phaseId }/employee`,
      {
        ...assignEmployeeData,
        ids: employeeOnPhaseIds,
      },
    );
  }

  addEmployeeInPhaseNote(employeeOnPhaseId: string, projectId: string, phaseId: string, note: string) {
    return this.httpClient.request('post',`${environment.api}/project/${projectId}/phase/${phaseId}/employee/${employeeOnPhaseId}/note`, {
      body: { note }
    });
  }

  editEmployeeInPhaseNote(employeeOnPhaseId: string, projectId: string, phaseId: string, noteId: string, note: string) {
    return this.httpClient.put(`${environment.api}/project/${projectId}/phase/${phaseId}/employee/${employeeOnPhaseId}/note/${noteId}`, {
      note,
    });
  }

  removeEmployeeInPhaseNote(employeeOnPhaseId: string, projectId: string, phaseId: string, noteId: string) {
    return this.httpClient.delete(`${environment.api}/project/${projectId}/phase/${phaseId}/employee/${employeeOnPhaseId}/note/${noteId}`);
  }

  checkConflictsWithProject(projectId: string, phaseId: string, employeeIds: string[]): Observable<EmployeeConflict[]> {
    let params = new HttpParams();

    employeeIds.forEach((id: string) => {
      params = params.append(`employeeIds[]`, id);
    });

    return this.httpClient.get<EmployeeConflict[]>(`${ environment.api }/project/${ projectId }/phase/${ phaseId }/employee/conflict`,
      { params });
  }
}
